import React, { useState } from "react";
import api from "../api/client";
import { useAIFlags } from "./AIAssistPanel";

// AI assistant for the Identity & Tools (RSAT) pages. The tech describes an
// AD / DNS / DHCP / Group Policy change in plain English; the AI proposes it
// as a HIGH-risk AIAction scoped to the current customer's domain. Nothing
// runs until it's approved here (or in AI Actions) — the AI only proposes.

interface ProposedAction {
  id: number;
  action_type: string;
  params: Record<string, unknown>;
  risk_tier: string;
  status: string;
  proposed_reason?: string | null;
  error_message?: string | null;
}

const EXAMPLES: Record<string, string> = {
  ad: "e.g. unlock the account for jsmith",
  dns: "e.g. add an A record 'vpn' pointing to 10.0.0.5 in example.com",
  dhcp: "e.g. reserve 10.0.0.50 for AA:BB:CC:DD:EE:FF in the main scope",
  gpo: "e.g. create a GPO called 'Kiosk Lockdown'",
  default: "Describe an AD / DNS / DHCP / Group Policy change…",
};

export default function IdentityAssistPanel({
  customerId,
  scope = "default",
}: {
  customerId: number | null;
  scope?: "ad" | "dns" | "dhcp" | "gpo" | "default";
}) {
  const flags = useAIFlags();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiText, setAiText] = useState("");
  const [actions, setActions] = useState<ProposedAction[]>([]);

  if (customerId == null || !flags) return null;

  const submit = async () => {
    if (!instruction.trim()) return;
    setBusy(true);
    setError(null);
    setActions([]);
    setAiText("");
    try {
      const r = await api.post("/api/v1/ai/identity-assist", {
        customer_id: customerId,
        instruction: instruction.trim(),
      });
      setAiText(r.data.text || "");
      const ids: number[] = r.data.proposed_action_ids || [];
      const fetched = await Promise.all(
        ids.map((id) =>
          api
            .get(`/api/v1/ai/actions/${id}`)
            .then((x) => x.data as ProposedAction)
            .catch(() => null)
        )
      );
      setActions(fetched.filter((a): a is ProposedAction => !!a));
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err.response?.data?.detail || err.message || "AI request failed");
    } finally {
      setBusy(false);
    }
  };

  const patch = (id: number, updated: Partial<ProposedAction>) =>
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, ...updated } : a)));

  return (
    <div className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/40 dark:to-blue-950/30 border border-violet-200 dark:border-violet-800/60 rounded-xl p-3 mb-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          ⚡ AI Assist
        </span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-violet-600 dark:text-violet-300 hover:underline"
        >
          {open ? "Hide" : "Ask AI to make a change"}
        </button>
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          {!flags.ai_enabled || !flags.ai_action_enabled ? (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {!flags.ai_enabled ? "AI mode is off." : "Action AI is disabled."} Enable it in{" "}
              <a className="text-blue-600 dark:text-blue-400 underline" href="/ai-settings">
                AI Settings
              </a>
              .
            </div>
          ) : (
            <>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={EXAMPLES[scope] || EXAMPLES.default}
                className="w-full text-sm border rounded-lg p-2 h-16 bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={submit}
                  disabled={busy || !instruction.trim()}
                  className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                >
                  {busy ? "thinking…" : "Propose"}
                </button>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  The AI proposes; changes wait for your approval below.
                </span>
              </div>

              {error && (
                <div className="text-xs bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                  {error}
                </div>
              )}

              {aiText && (
                <div className="text-xs whitespace-pre-wrap bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 text-gray-800 dark:text-gray-100">
                  {aiText}
                </div>
              )}

              {actions.length === 0 && aiText && !busy && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  No concrete action proposed — try rephrasing with the specific object (user, record,
                  scope, or GPO) you want changed.
                </div>
              )}

              {actions.map((a) => (
                <ActionCard key={a.id} customerId={customerId} action={a} onChange={(u) => patch(a.id, u)} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ActionCard({
  action,
  onChange,
}: {
  customerId: number;
  action: ProposedAction;
  onChange: (u: Partial<ProposedAction>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    // Every RSAT action is HIGH risk → require an approval note.
    const note = window.prompt(
      `Approve and run this change?\n\n${action.action_type}\n${JSON.stringify(action.params, null, 2)}\n\nEnter an approval note (required):`,
      ""
    );
    if (!note) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/approve-and-execute`, { note });
      onChange({ status: r.data.status });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err.response?.data?.detail || err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Reject this proposed change?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/reject`, {});
      onChange({ status: r.data.status });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err.response?.data?.detail || err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const done =
    action.status === "succeeded"
      ? { txt: "✓ applied", cls: "text-green-700 dark:text-green-400" }
      : action.status === "failed"
        ? { txt: "✗ failed", cls: "text-red-700 dark:text-red-400" }
        : action.status === "rejected"
          ? { txt: "rejected", cls: "text-gray-500 dark:text-gray-400 italic" }
          : null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className="text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200">
          {action.risk_tier} risk
        </span>
        <code className="text-[10px] bg-gray-100 dark:bg-gray-900/60 dark:text-gray-100 px-1 rounded">
          {action.action_type}
        </code>
      </div>
      <pre className="text-[10px] bg-gray-50 dark:bg-gray-900/50 rounded p-1 overflow-auto text-gray-700 dark:text-gray-200">
        {JSON.stringify(action.params, null, 2)}
      </pre>
      {action.proposed_reason && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{action.proposed_reason}</p>
      )}
      {done ? (
        <div className={`mt-1.5 text-[11px] font-mono ${done.cls}`}>
          {done.txt}
          {action.status === "failed" && action.error_message ? `: ${action.error_message}` : ""}
        </div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <button
            onClick={approve}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold disabled:opacity-50"
          >
            {busy ? "running…" : "▶ Approve & run"}
          </button>
          <button
            onClick={reject}
            disabled={busy}
            className="text-[11px] px-2 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded"
          >
            Reject
          </button>
          <a href="/ai-actions" className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline ml-auto">
            #{action.id} in AI Actions
          </a>
        </div>
      )}
      {error && <div className="text-[10px] text-red-700 dark:text-red-400 mt-1">{error}</div>}
    </div>
  );
}
